/**
 * Display formatters — used across the app to format dates, currency, and durations for UI display.
 *
 * These are the canonical versions. Do not duplicate format logic locally in components.
 */

import { SITE_URL } from '@/shared/seo/seoRouting';
import type { SearchFilters } from '@mondivago/types';

// ─── Date utilities ───

/** Pads a number to 2 digits (e.g. 3 → "03") */
export function padTwo(n: number) {
  return String(n).padStart(2, '0');
}

/**
 * Safely parses any date string or Date object into a UTC Date object at 00:00:00Z.
 * Ensures date-only strings (e.g. "2026-08-10") stay at UTC midnight without timezone shifting.
 */
export function toUtcDate(input: string | Date | null | undefined): Date | null {
  if (!input) return null;
  if (input instanceof Date) {
    return isNaN(input.getTime()) ? null : input;
  }
  const str = String(input).trim();
  if (!str) return null;

  const dateOnlyMatch = /^(\d{4})-(\d{2})-(\d{2})/.exec(str);
  if (dateOnlyMatch) {
    const year = Number(dateOnlyMatch[1]);
    const month = Number(dateOnlyMatch[2]) - 1;
    const day = Number(dateOnlyMatch[3]);
    return new Date(Date.UTC(year, month, day));
  }

  const d = new Date(str);
  return isNaN(d.getTime()) ? null : d;
}

/**
 * Converts any Date or date string to a YYYY-MM-DD key.
 * Formats local date components for Date objects (like rrule occurrence dates).
 */
export function toDateKey(input: string | Date | null | undefined): string {
  if (!input) return '';
  if (typeof input === 'string') {
    if (/^\d{4}-\d{2}-\d{2}$/.test(input)) return input;
    const match = /^(\d{4}-\d{2}-\d{2})/.exec(input);
    if (match && !input.includes('T')) return match[1];
  }
  const d = typeof input === 'string' ? new Date(input) : input;
  if (isNaN(d.getTime())) return '';
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/**
 * Formats a Date or date string for UI display in UTC (e.g. "10 Aug 2026").
 * Prevents local timezone offsets from shifting dates.
 */
export function formatDateDisplay(
  input: string | Date | null | undefined,
  options: Intl.DateTimeFormatOptions = { day: '2-digit', month: 'short', year: 'numeric' },
  locale: string = 'en-US'
): string {
  const d = toUtcDate(input);
  if (!d) return '';
  return new Intl.DateTimeFormat(locale, { ...options, timeZone: 'UTC' }).format(d);
}

/**
 * Formats a time string (from ISO datetime) for UI display (e.g. "12:00 PM").
 */
export function formatTimeDisplay(
  input: string | Date | null | undefined,
  options: Intl.DateTimeFormatOptions = { hour: '2-digit', minute: '2-digit' },
  locale: string = 'en-US'
): string {
  if (!input) return '';
  const d = typeof input === 'string' ? new Date(input) : input;
  if (isNaN(d.getTime())) return '';
  return new Intl.DateTimeFormat(locale, { ...options, timeZone: 'UTC' }).format(d);
}

/**
 * Formats both date and time (e.g. "10 Aug 2026, 12:00 PM").
 * Uses tour_date for the date and availability start_datetime for the time.
 */
export function formatBookingDateTime(
  tourDate: string | null | undefined,
  startDatetime: string | Date | null | undefined,
  locale: string = 'en-US'
): string {
  if (!tourDate && !startDatetime) return '';

  const dateStr = formatDateDisplay(
    tourDate || startDatetime,
    {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    },
    locale
  );

  if (tourDate && startDatetime) {
    const timeStr = formatTimeDisplay(startDatetime);
    return timeStr ? `${dateStr}, ${timeStr}` : dateStr;
  }

  if (startDatetime && !tourDate) {
    const d = typeof startDatetime === 'string' ? new Date(startDatetime) : startDatetime;
    if (!isNaN(d.getTime())) {
      return new Intl.DateTimeFormat(locale, {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        timeZone: 'UTC',
      }).format(d);
    }
  }

  return dateStr;
}

/**
 * Generates a Google Calendar URL with clean UTC start and end timestamps.
 */
export function toGoogleCalendarUrl(
  title: string,
  dateStr: string | null | undefined,
  timeStr: string = '10:00'
): string {
  if (!dateStr) return '#';
  const cleanDate = dateStr.split('T')[0];
  const cleanTime = timeStr ? (timeStr.length === 5 ? `${timeStr}:00` : timeStr) : '10:00:00';
  const start = new Date(`${cleanDate}T${cleanTime}Z`);
  const end = new Date(start.getTime() + 2 * 60 * 60 * 1000);
  const fmt = (d: Date) => d.toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z';
  const params = new URLSearchParams({
    action: 'TEMPLATE',
    text: title,
    dates: `${fmt(start)}/${fmt(end)}`,
    details: 'Booking confirmed with Mondivago',
  });
  return `https://calendar.google.com/calendar/render?${params.toString()}`;
}

/**
 * Computes relative time until a date (e.g. "Today", "Tomorrow", "In 3 days").
 * Uses tour_date if available, otherwise start_datetime.
 */
export function getRelativeTime(
  tourDate: string | null | undefined,
  startDatetime: string | null | undefined
): string | null {
  const dateStr = tourDate ? `${tourDate.split('T')[0]}T00:00:00Z` : startDatetime;
  if (!dateStr) return null;
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return null;

  const now = new Date();
  const diffMs = d.getTime() - now.getTime();
  if (diffMs < 0) return null;

  const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));
  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Tomorrow';
  if (diffDays < 7) return `In ${diffDays} days`;
  if (diffDays < 30) return `In ${Math.floor(diffDays / 7)} wks.`;
  return `In ${Math.floor(diffDays / 30)} mos.`;
}

// ─── Currency ───

/** Formats a number as EUR currency (e.g. 1234.50 → "€1,234.50") */
export function formatEuro(value: number) {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
}

// ─── Relative Time ───

/** Compact relative time: "5m", "2h", "3d", "1w" */
export function formatElapsedCompact(value: string | Date, now: Date = new Date()): string {
  const date = value instanceof Date ? value : new Date(value);
  const diffSeconds = Math.max(0, Math.floor((now.getTime() - date.getTime()) / 1000));

  if (diffSeconds < 60) return `${String(diffSeconds)}s`;
  if (diffSeconds < 3600) return `${String(Math.floor(diffSeconds / 60))}m`;
  if (diffSeconds < 86400) return `${String(Math.floor(diffSeconds / 3600))}h`;
  if (diffSeconds < 604800) return `${String(Math.floor(diffSeconds / 86400))}d`;
  if (diffSeconds < 2592000) return `${String(Math.floor(diffSeconds / 604800))}w`;

  return date.toLocaleDateString('en-US', { day: '2-digit', month: 'short', year: 'numeric', timeZone: 'UTC' });
}

/** Detailed relative time: "2h 30m", "3d 12h" */
export function formatElapsedDetailed(value: string | Date, now: Date = new Date()): string {
  const date = value instanceof Date ? value : new Date(value);
  const diffSeconds = Math.max(0, Math.floor((now.getTime() - date.getTime()) / 1000));

  if (diffSeconds >= 2592000) {
    return date.toLocaleDateString('en-US', { day: '2-digit', month: 'short', year: 'numeric', timeZone: 'UTC' });
  }

  const weeks = Math.floor(diffSeconds / 604800);
  if (weeks >= 1) {
    const days = Math.floor((diffSeconds % 604800) / 86400);
    return days > 0 ? `${String(weeks)}w ${String(days)}d` : `${String(weeks)}w`;
  }

  const days = Math.floor(diffSeconds / 86400);
  const hours = Math.floor((diffSeconds % 86400) / 3600);
  const minutes = Math.floor((diffSeconds % 3600) / 60);
  const seconds = diffSeconds % 60;

  const chunks: string[] = [];
  if (days > 0) {
    chunks.push(`${String(days)}d`);
    if (hours > 0) chunks.push(`${String(hours)}h`);
  } else if (hours > 0) {
    chunks.push(`${String(hours)}h`);
    if (minutes > 0) chunks.push(`${String(minutes)}m`);
  } else if (minutes > 0) {
    chunks.push(`${String(minutes)}m`);
  } else {
    chunks.push(`${String(seconds)}s`);
  }

  return chunks.join(' ');
}

/** Checks if a string looks like a relative duration label (e.g. "2h 30m") */
export function isRelativeDurationLabel(label: string): boolean {
  return /^\d+[smhdw]( \d+[smhdw])*$/.test(label);
}

// ─── Promo Code Helpers ───

/** Checks if a date string is in the past */
export function isExpired(dateStr: string) {
  return new Date(dateStr) <= new Date();
}

/** Formats a date as "12 Jan 2026" */
export function formatDateShort(dateStr: string) {
  return formatDateDisplay(dateStr, { day: '2-digit', month: 'short', year: 'numeric' });
}

/** Human-readable time remaining until a date */
export function getTimeLeft(dateStr: string) {
  const diff = new Date(dateStr).getTime() - Date.now();
  if (diff <= 0) return 'Expired';
  const days = Math.floor(diff / 86400000);
  const hours = Math.floor((diff % 86400000) / 3600000);
  if (days > 0) return `${days}d left`;
  if (hours > 0) return `${hours}h left`;
  return 'Soon';
}

/** Percentage of max uses consumed (0 for unlimited) */
export function getUsagePercent(maxUses: number, currentUses: number) {
  if (maxUses === 0) return 0;
  return Math.round((currentUses / maxUses) * 100);
}

/** Build a promo shareable URL */
export function buildPromoUrl(code: string, _tourSlug: string, tourPath: string) {
  return `${SITE_URL}${tourPath}?promo=${code}`;
}

// ─── Search URL ───

/**
 * Build a `/search?...` URL from SearchFilters.
 * Used by search sub-pages (city, country, category) to navigate to the master search page.
 */
export function buildSearchUrl(filters: SearchFilters): string {
  const params = new URLSearchParams();
  if (filters.location?.trim()) params.set('q', filters.location.trim());
  if (filters.date) params.set('date', toDateKey(filters.date));
  if (filters.adults) params.set('adults', String(filters.adults));
  if (filters.children) params.set('children', String(filters.children));
  return `/search?${params.toString()}`;
}

// ─── Flags ───

/**
 * Converts an ISO 3166-1 alpha-2 country code to a flag emoji.
 * Returns 🌎 for invalid/missing codes.
 */
export const getFlagEmoji = (iso2: string | null | undefined): string => {
  const clean = iso2?.trim();
  if (clean?.length !== 2) return '🌎';
  return clean.toUpperCase().replace(/./g, (char) => String.fromCodePoint(char.charCodeAt(0) + 127397));
};
